import React, { useEffect, useState } from 'react'
import { GAbutton, GAtrack } from '../../services/ga.ts'
import { useWalletContext } from '../WalletContext.tsx'
import BoxHeaderNew, { DEFAULT_SLIPPAGE } from '../BoxHeader/BoxHeader.tsx'
import PrimaryButton from '../PrimaryButton.tsx'
import { useStoreActions, useStoreState } from '../../model/hooks.ts'
import { calcTickRangesFromPercent, calculateTickFromPrice, findClosestTick, normalizeForTokens } from '../../blockchainUtils/math.ts'
import { getSortedList, toWei } from '../../model/tokens/helper.ts'
import Select from '../TokenSelectorNew/Select.tsx'
import { Network } from '../../services/token.service.js'
import { possiblePools } from '../PositionsV3.tsx'
import { getNFTFactoryAddress, mint } from '../../services/nft_pool.ts'
import { getAllCLPools, getCLPool, getIsPoolTokensReversed } from '../../model/clpool/helper.ts'
import { getCurrentRatio, getCurrentTick } from '../../services/amm_v3.ts'
import { _getAmount0FromAmount1, _getAmount1FromAmount0 } from '../../blockchainUtils/positionAmountsMath.ts'
import BigNumber from 'bignumber.js'
import FeeTierStep from './Steps/FeeTierStep.tsx'
import RangeStep from './Steps/RangeStep.tsx'
import DepositStep from './Steps/DepositStep.tsx'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CreatePoolIcon } from '../../svgIcons/CreatePoolIcon.tsx'
import Footer from '../Footer/Footer.tsx'
import { pageStyle, pageStyleMobile } from '../Main.tsx'
import Header from '../Header/Header.tsx'
import { tokenAddresses } from '../IncentivePage/IncentivePage.tsx'
import { loadPoolsData } from '../charts/chartsApi.ts'
import { getRangeForFee } from '../../utils.ts'
import { RewardBadgeIcon } from '../../svgIcons/RewardBadgeIcon.tsx'
import BackButton from '../BackButton/BackButton.tsx'
import { EthAddress, MAX_TICK, getDepositTotalAmount, getIsNextCreatePositionStepDisabled, getMinMaxTickState, getNewCurrentRatio, getUpdatedCratePositionUrl } from './helper.ts'
import { Token } from '../../atoms.ts'
import { useTransactionFollow } from '../../hooks/useTransactionFollow.ts'

const errors = {
    invalidFee: 'Enter a valid fee',
    outOfRange: 'Your position will not earn fees or be used in trades until the market price moves into your range'
}

export default function CreatePosition() {
    let [searchParams] = useSearchParams()
    const { walletAddress: connectedAddress, networkName: network } = useWalletContext()
    const [token1, setToken1] = useState('')
    const [token2, setToken2] = useState('')
    const [mintTxHash, setMintTxHash] = useState('')
    const [fee, setFee] = useState(undefined as unknown as number)
    const [token1Amount, setToken1Amount] = useState('')
    const [token2Amount, setToken2Amount] = useState('')
    const [leadingToken, setLeadingToken] = useState(0) // 0 is no token, 1/2 is the last updated token
    const tokens = useStoreState((state) => state.tokens.getTokensList(network ? network : 'mainnet-alpha'))
    const getToken = useStoreState((state) => state.tokens.getToken)
    const [stepNumber, setStepNumber] = useState(2)
    const [error, setError] = useState('')
    const getUsdPrice = useStoreState((store) => store.pricing.getPrice)
    const [tickAmount, setTickAmount] = useState(10 as 1 | 2 | 10 | 60 | 200) // this will set based on pool fee
    const [minTick, setMinTick] = useState(0)
    const [maxTick, setMaxTick] = useState(0)
    const [currentTick, setCurrentTick] = useState(0)
    const [currentRatio, setCurrentRatio] = useState(0)
    const loadTransaction = useStoreActions((actions) => actions.transaction.loadTransaction)
    const loadPositons = useStoreActions((actions) => actions.clPools.loadPositons)
    const [isAddPosition, setIsAddPosition] = useState(false)
    const [isLoadingTicks, setIsLoadingTicks] = useState(true)
    const loadBalances = useStoreActions((actions) => actions.tokens.loadBalances)
    const [isTxInProgress, setIsTxInProgress] = useState(false)
    const [feeOptions, setFeeOptions] = useState(undefined as unknown as number[])
    const [isTokensReversedFromPool, setIsTokensReversedFromPool] = useState(false)
    const [isRatioReversed, setIsRatioReversed] = useState(undefined as unknown as boolean)
    const [slippage, setSlippage] = useState(Number(DEFAULT_SLIPPAGE))
    const [rangeSelected, setRangeSelected] = useState(0.5 as unknown as number)
    const [isMaxRangeSelected, setIsMaxRangSelected] = useState(false)
    const [customRange, setCustomRange] = useState(undefined as unknown as number)
    const [rangeOptions, setRangeOptions] = useState([0.5, 1.5, 3, 5])
    const selectedPool = getCLPool(network as Network, getToken(network as Network, token1)!, getToken(network as Network, token2)!, fee * 10000)
    const [isCreatePoolPage, setIsCreatePoolPage] = useState(false)
    const [isCalculatingRatioNewPool, setIsCalculatingRatioNewPool] = useState(false)
    const [selectedDefaultFee, setSelectedDefaultFee] = useState(undefined as unknown as number)
    const [poolFees, setPoolsFees] = useState([] as { token0: string; token1: string; fee: number }[])
    const navigate = useNavigate()
    const [userSelectedMaxTick, setUserSelectedMaxTick] = useState(undefined as unknown as number)
    const [userSelectedMinTick, setUserSelectedMinTick] = useState(undefined as unknown as number)
    useTransactionFollow(mintTxHash, updateDataAndNavigateBack, () => {
        setMintTxHash('')
        setIsTxInProgress(false)
        sendGAEvent('mint_tx_done_reverted')
    })

    useEffect(() => {
        if (network) loadStaticPoolsData()
    }, [network])

    useEffect(() => {
        if (searchParams.get('token0') || searchParams.get('token1') || searchParams.get('default_fee')) {
            setIsAddPosition(true)
            if (searchParams.get('token0')) setToken1(searchParams.get('token0')!)
            if (searchParams.get('token1')) setToken2(searchParams.get('token1')!)
            setFee(Number(searchParams.get('default_fee')!))
            if (searchParams.get('maxTick')) setUserSelectedMaxTick(Number(searchParams.get('maxTick')))
            else setUserSelectedMaxTick(undefined as unknown as number)
            if (searchParams.get('minTick')) setUserSelectedMinTick(Number(searchParams.get('minTick')))
            else setUserSelectedMinTick(undefined as unknown as number)
            if (searchParams.get('range')) setRangeSelected(Number(searchParams.get('range')))
            else setRangeSelected(undefined as unknown as number)
            if (searchParams.get('default_fee')) setSelectedDefaultFee(Number(searchParams.get('default_fee')))
        }
    }, [searchParams])

    useEffect(() => {
        if (!searchParams.get('token0')) updateUrlSearchParams({ token1Address: EthAddress })
        if (network === 'mainnet-alpha') GAtrack('route_pools', 'route_pools', 'pools', -1)
    }, [])

    useEffect(() => {
        if (token1 === tokenAddresses.STRK || token2 === tokenAddresses.STRK) setSlippage(2)
        else setSlippage(Number(DEFAULT_SLIPPAGE))
    }, [token1, token2])

    useEffect(() => {
        const intervalId = setInterval(() => intervalLoadData(), 30000)
        return () => clearInterval(intervalId)
    }, [selectedPool, isCreatePoolPage])

    useEffect(() => {
        if (selectedPool) loadCurrentTick()
    }, [selectedPool])

    useEffect(() => {
        if (token1 && token2) loadPoolsTokenData()
    }, [token1, token2])

    useEffect(() => {
        if (customRange && customRange > 0) updateMinMaxTicksDefault(currentRatio, tickAmount, customRange)
    }, [customRange])

    useEffect(() => {
        if (currentTick > maxTick) setToken1Amount('')
        if (currentTick < minTick) setToken2Amount('')
    }, [maxTick, minTick, currentTick])

    async function updateDataAndNavigateBack() {
        await loadPositons({ network: network as Network, address: connectedAddress! })
        loadBalances({ network: network as Network, walletAddress: connectedAddress! })
        setMintTxHash('')
        sendGAEvent('mint_tx_done_accepted')
        navigate('/positions')
    }

    useEffect(() => {
        if (poolFees.length > 0) updateFeeOptions()
    }, [poolFees, token1, token2])

    useEffect(() => {
        if (fee && token1 && token2) {
            setStepNumber(3)
            setToken1Amount('')
            setToken2Amount('')
        }
    }, [fee, token1, token2])

    useEffect(() => {
        if (selectedPool && token1 && token2) {
            const isReveresed = getIsPoolTokensReversed(selectedPool, token1, token2)
            setIsTokensReversedFromPool(isReveresed)
        }
    }, [selectedPool, token1, token2])

    useEffect(() => {
        if (currentRatio) setIsLoadingTicks(false)
    }, [currentRatio])

    useEffect(() => {
        if (fee && fee > 10) setError(errors.invalidFee)
        if (fee && fee < 10) setError('')
        if (fee) onFeeUpdated()
    }, [fee])

    useEffect(() => {
        if (isMaxRangeSelected) onFullRangeSelected()
    }, [isMaxRangeSelected])

    useEffect(() => {
        if (rangeSelected && currentRatio && token2 && token1 && (!userSelectedMaxTick || !userSelectedMinTick)) updateMinMaxTicksDefault(currentRatio, tickAmount, rangeSelected)
        if (rangeSelected && currentRatio && ((!isAddPosition && !isCreatePoolPage) || maxTick === 0)) updateMinMaxTicksDefault(currentRatio, tickAmount, rangeSelected)
    }, [rangeSelected, currentRatio, token2, token1, fee])

    useEffect(() => {
        let useFee = selectedDefaultFee ? selectedDefaultFee : fee
        const newRanges = getRangeForFee(useFee)
        const defaultRange = newRanges[0]
        setRangeOptions(newRanges)
        if (defaultRange !== rangeSelected && token1 && !userSelectedMinTick && !userSelectedMaxTick && !rangeSelected) {
            // if (defaultRange !== rangeSelected && token1 && !userSelectedMinTick && !userSelectedMaxTick) {
            // do i leave it
            updateUrlSearchParams({ token1Address: token1, token2Address: token2, fee: fee, maxTick: undefined, minTick: undefined, range: defaultRange })
        }
    }, [fee, selectedDefaultFee])

    function updateMinMaxTicksDefault(currentRatio: number, tickAmount: number, range: number) {
        const { min_tick, max_tick } = calcTickRangesFromPercent(currentRatio, tickAmount, range)
        setMaxTick(max_tick)
        setMinTick(min_tick)
    }

    function updateUrlSearchParams(params: { token1Address?: string; token2Address?: string; fee?: number; maxTick?: number; minTick?: number; range?: number }) {
        const url = getUpdatedCratePositionUrl(params)
        navigate(url)
    }

    function createEventName(name: string): string {
        // Function to create event name with wallet name
        try {
            const wname = window.getStarknet?.()?.name!
            return `${name}_${wname}`
        } catch {}
        return name
    }

    function sendGAEvent(name: string) {
        if (network === 'mainnet-alpha') {
            const ename = createEventName(name)
            GAtrack(ename, ename, ename)
        }
    }

    async function loadStaticPoolsData() {
        const repsonse = await loadPoolsData(network)
        const poolFeesData = [] as { token0: string; token1: string; fee: number }[]
        repsonse.map((pool) => {
            poolFeesData.push({ token0: pool.token0.address, token1: pool.token1.address, fee: pool.pool_fee * 10000 })
        })
        setPoolsFees(poolFeesData)
    }

    async function updateIsCreatePoolPage(isNoFeesYet: boolean) {
        if (!connectedAddress) return
        setIsLoadingTicks(false)
        setIsCreatePoolPage(true)
        setIsCalculatingRatioNewPool(true)
        if (isNoFeesYet) setFeeOptions([10, 100, 500, 3000, 10000])
        setStepNumber(3)
    }

    function onSelectFeeCreatePool(fee: number) {
        if (!token1 || !token2) return
        if (!connectedAddress) return
        setIsCreatePoolPage(true)
        console.log('2222 onSelectFeeCreatePool')

        updateUrlSearchParams({ token1Address: token1, token2Address: token2, fee: fee })
        setSelectedDefaultFee(undefined as unknown as number)
        setStepNumber(3)
    }

    async function intervalLoadData() {
        if (connectedAddress && network) loadBalances({ network: network as Network, walletAddress: connectedAddress! })
        if (!isCreatePoolPage) loadTickInformation(true)
    }

    function getFeeList() {
        const fees: number[] = []
        poolFees.forEach((pool) => {
            if ((pool.token0 === token1 && pool.token1 === token2) || (pool.token1 === token1 && pool.token0 === token2)) fees.push(pool.fee)
        })
        return fees
    }

    function updateFeeOptions() {
        const fees = getFeeList()
        if (!fees) return
        if (fees.length === 0 && token1 && token2) return updateIsCreatePoolPage(true)
        const feeList = fees.sort((a, b) => b - a)
        const selectFee = feeList && feeList.length > 1 ? feeList[1] : feeList[0]
        const updatedFee = selectedDefaultFee && feeList.includes(selectedDefaultFee * 10000) ? selectedDefaultFee : selectFee / 10000
        if (!selectedDefaultFee) updateUrlSearchParams({ token1Address: token1, token2Address: token2, fee: updatedFee }) // do i need this if?
        setIsCreatePoolPage(false)
        setFeeOptions(feeList)
    }

    function onFullRangeSelected() {
        setMaxTick(MAX_TICK)
        setMinTick(-MAX_TICK)
    }

    function onUpdateCurrentRatio(number: number) {
        if (Number(number) <= 0) return setCurrentRatio(0)
        const newRatio = getNewCurrentRatio(network as Network, number, token1, token2)
        setCurrentRatio(Number(newRatio.toFixed()))
        setIsCalculatingRatioNewPool(false)
        const currentTick = calculateTickFromPrice(newRatio.toNumber(), tickAmount)
        setCurrentTick(currentTick)
    }

    function onSetFee(feeValue: number) {
        const fees = getFeeList()
        if (fees && fees.length > 0) {
            setIsCreatePoolPage(false)
            setSelectedDefaultFee(undefined as unknown as number)
            updateUrlSearchParams({ token1Address: token1, token2Address: token2, fee: feeValue, maxTick: undefined, minTick: undefined, range: undefined })
        } else onSelectFeeCreatePool(feeValue)
    }

    async function callMint() {
        if (!selectedPool && !isCreatePoolPage) return
        sendGAEvent('mint_button_clicked')

        setIsTxInProgress(true)
        const isTokensSameOrder = isCreatePoolPage ? !new BigNumber(token1).gt(token2) : token1.toLowerCase() === selectedPool!.token1.address
        let toke1Amount = isTokensSameOrder ? token1Amount : token2Amount
        let toke2Amount = isTokensSameOrder ? token2Amount : token1Amount
        if (toke1Amount === '') toke1Amount = '0'
        if (toke2Amount === '') toke2Amount = '0'
        const toke1 = isTokensSameOrder ? token1 : token2
        const toke2 = isTokensSameOrder ? token2 : token1

        try {
            const { maxTickState, minTickState } = getMinMaxTickState(userSelectedMinTick, minTick, userSelectedMaxTick, maxTick)

            const res = await mint(
                network!,
                connectedAddress!,
                toke1,
                toke2,
                fee * 10000,
                isMaxRangeSelected ? findClosestTick(-MAX_TICK, tickAmount) + MAX_TICK : minTickState + MAX_TICK,
                isMaxRangeSelected ? findClosestTick(MAX_TICK, tickAmount) + MAX_TICK : maxTickState + MAX_TICK,
                toWei(toke1Amount, getToken(network as Network, toke1)!),
                toWei(toke2Amount, getToken(network as Network, toke2)!),
                toWei((Number(toke1Amount) * ((100 - slippage) / 100)).toString(), getToken(network as Network, toke1)!),
                toWei((Number(toke2Amount) * ((100 - slippage) / 100)).toString(), getToken(network as Network, toke2)!),
                getNFTFactoryAddress(network as Network),
                isCreatePoolPage ? new BigNumber(currentRatio).sqrt().multipliedBy(new BigNumber(2).pow(96)).toString() : undefined
            )

            const timestampSent = new Date().getTime()

            if (res.success) {
                setToken1Amount('')
                setToken2Amount('')
                loadTransaction({
                    txPayload: { [res.tx!.hash]: { ...res.tx!, labelFields: { timestampSent: timestampSent, sentCurrentTick: currentTick } } },
                    isGoogleLog: network === 'mainnet-alpha'
                })

                sendGAEvent('mint_tx_sent')
                setMintTxHash(res.tx!.hash)
                updateUrlSearchParams({ token1Address: token1, token2Address: token2, fee: fee, maxTick: undefined, minTick: undefined })
            } else {
                setIsTxInProgress(false)
                sendGAEvent('mint_tx_aborted')
            }
        } catch (e: any) {
            setIsTxInProgress(false)
            console.log('Failed to send mint order ' + e.message)
        }
    }

    async function loadPoolsTokenData() {
        try {
            const t1 = getToken(network as Network, token1)!
            const t2 = getToken(network as Network, token2)!
            getAllCLPools(network as Network, t1, t2)
        } catch (e) {
            setIsLoadingTicks(false)
        }
    }

    async function loadCurrentTick() {
        setIsLoadingTicks(true)
        loadTickInformation()
    }

    async function loadTickInformation(isSkipReset = false) {
        try {
            if (!selectedPool) return

            if (isRatioReversed === undefined) {
                if (selectedPool.token1.symbol === 'ETH' && !isSkipReset) setIsRatioReversed(true)
                else if (selectedPool.token2.symbol === 'ETH' && !isSkipReset) setIsRatioReversed(false)
            }

            const ratio = await getCurrentRatio(network as Network, selectedPool.address)
            const tick = await getCurrentTick(network as Network, selectedPool.address)

            setIsLoadingTicks(false)
            if (ratio && ratio.toString) setCurrentRatio(ratio.toString())

            if (tick || tick === 0) setCurrentTick(tick)
        } catch (e: any) {
            console.log('loadTickInformation ERROR ', e.message)
        }
    }

    function getClosestTick(price: string) {
        const isReversed = !new BigNumber(token1).gt(token2)
        const token1Obj = getToken(network!, isReversed ? token2 : token1)
        const token2Obj = getToken(network!, isReversed ? token1 : token2)
        const normalizedPrice = normalizeForTokens(new BigNumber(price), token1Obj!, token2Obj!)
        const tick = calculateTickFromPrice(normalizedPrice.toNumber(), tickAmount) * (isRatioReversed ? 1 : -1)
        const closestTIck = findClosestTick(Number(tick), tickAmount)
        return closestTIck
    }

    function updateMinTickBasedOnPrice(newLowPrice: string) {
        if (Number(newLowPrice) <= 0) return
        const closestTick = getClosestTick(newLowPrice)
        updateUrlSearchParams({ token1Address: token1, token2Address: token2, fee: fee, minTick: closestTick, maxTick: userSelectedMaxTick })
    }

    function updateMaxTickBasedOnPrice(newMaxPrice: string) {
        if (Number(newMaxPrice) <= 0) return
        const closestTick = getClosestTick(newMaxPrice)
        updateUrlSearchParams({ token1Address: token1, token2Address: token2, fee: fee, maxTick: closestTick, minTick: userSelectedMinTick })
    }

    function updateMinTick(addNumber: number) {
        setIsMaxRangSelected(false)
        const value = userSelectedMinTick || Number(userSelectedMinTick) === 0 ? userSelectedMinTick : minTick
        const newVal = value + addNumber
        const closestTIck = findClosestTick(newVal, tickAmount)
        updateUrlSearchParams({ token1Address: token1, token2Address: token2, fee: fee, minTick: closestTIck, maxTick: userSelectedMaxTick, range: undefined })
    }

    function updateMaxTick(addNumber: number) {
        const value = userSelectedMaxTick || Number(userSelectedMaxTick) === 0 ? userSelectedMaxTick : maxTick
        setIsMaxRangSelected(false)
        const newVal = value + addNumber
        const closestTIck = findClosestTick(newVal, tickAmount)
        updateUrlSearchParams({ token1Address: token1, token2Address: token2, fee: fee, maxTick: closestTIck, minTick: userSelectedMinTick, range: undefined })
    }

    function onFeeUpdated() {
        if (fee === 0.001) return setTickAmount(1)
        if (fee === 0.01) return setTickAmount(2)
        if (fee === 0.05) return setTickAmount(10)
        else if (fee > 0.05 && fee <= 0.3) return setTickAmount(60)
        return setTickAmount(200)
    }

    useEffect(() => {
        if ((token1Amount && Number(token1Amount) > 0) || (token2Amount && Number(token2Amount) > 0)) setAmounts()
    }, [token1, token2, token1Amount, token2Amount, network, minTick, maxTick, currentRatio, userSelectedMaxTick, userSelectedMinTick])

    function setAmounts() {
        if (!token1Amount && !token2Amount) return
        if (leadingToken === 0) return
        try {
            const { maxTickState, minTickState } = getMinMaxTickState(userSelectedMinTick, minTick, userSelectedMaxTick, maxTick)

            if (token1 && token2) {
                const isReversed = BigNumber(token1).gt(token2)
                const token1Pool = isReversed ? getToken(network!, token2!) : getToken(network!, token1)!
                const token2Pool = isReversed ? getToken(network!, token1!) : getToken(network!, token2)!

                const sqrtRatio = BigNumber(currentRatio).sqrt()
                if (leadingToken === 1) {
                    const amountToken2 = !isReversed
                        ? _getAmount1FromAmount0(token1Amount, token1Pool!, token2Pool!, minTickState, maxTickState, sqrtRatio)
                        : _getAmount0FromAmount1(token1Amount, token1Pool!, token2Pool!, minTickState, maxTickState, sqrtRatio)

                    setToken2Amount(Number(amountToken2) > 0 ? amountToken2.toString() : '')
                } else {
                    const amounttoken1 = !isReversed
                        ? _getAmount0FromAmount1(token2Amount, token1Pool!, token2Pool!, minTickState, maxTickState, sqrtRatio)
                        : _getAmount1FromAmount0(token2Amount, token1Pool!, token2Pool!, minTickState, maxTickState, sqrtRatio)
                    setToken1Amount(Number(amounttoken1) > 0 ? amounttoken1.toString() : '')
                }
            }
        } catch (e: any) {
            if (leadingToken === 1) setToken2Amount('0')
            else setToken1Amount('0')
            console.log('FAILED ', e.message)
        }
    }

    const handleTokenAmountChange = (token: string, amount: string) => {
        // fixme: consider limit decimals input according to token decimals
        const regex = /^(([0-9]+[.]?[0-9]*)|([.][0-9]+))$/
        if (token === token1 && (amount === '' || regex.test(amount))) {
            setToken1Amount(amount)
            setLeadingToken(1)
        }
        if (token === token2 && (amount === '' || regex.test(amount))) {
            setToken2Amount(amount)
            setLeadingToken(2)
        }
    }

    function getIsNextDisabled() {
        return getIsNextCreatePositionStepDisabled(
            isTxInProgress,
            stepNumber,
            token1,
            token2,
            fee,
            isCreatePoolPage,
            isCalculatingRatioNewPool,
            isTokensReversedFromPool,
            network as Network,
            token1Amount,
            token2Amount,
            userSelectedMaxTick,
            userSelectedMaxTick,
            minTick,
            maxTick,
            currentTick
        )
    }

    function handleStepChange(isNext: boolean) {
        if (stepNumber === 4) return callMint()
        if (isNext && stepNumber < 4) return setStepNumber(stepNumber + 1)
        if (stepNumber > 1) return setStepNumber(stepNumber - 1)
    }

    const handleToken1Select = (address: string) => {
        if (address === token1) return
        updateUrlSearchParams({ token1Address: address, token2Address: token2, fee: fee, minTick: undefined, maxTick: undefined, range: rangeOptions[0] })
        setToken1Amount('')
        setToken2Amount('')
        setIsRatioReversed(undefined as unknown as boolean)
        GAbutton('pool-token1')
    }

    const handleToken2Select = (address: string) => {
        if (address === token2) return
        updateUrlSearchParams({ token1Address: token1, token2Address: address, fee: fee, minTick: undefined, maxTick: undefined, range: rangeOptions[0] })
        setToken1Amount('')
        setToken2Amount('')
        setIsRatioReversed(undefined as unknown as boolean)
        GAbutton('pool-token2')
    }

    function renderStepFeeTier() {
        return (
            <FeeTierStep
                network={network as Network}
                onSelectFeeCreatePool={onSelectFeeCreatePool}
                isCreatePool={isCreatePoolPage}
                isLoading={isLoadingTicks}
                error={error}
                fee={fee}
                feeOptions={feeOptions}
                setFee={(fee) => onSetFee(fee as any)}
                token1={token1}
                token2={token2}
            />
        )
    }

    function renderStepDeposit() {
        return (
            <DepositStep
                renderActionButton={renderActionButton}
                renderOutOfRangeInput={renderOutOfRangeInput}
                renderStepIdentifier={renderStepIdentifier}
                token1={token1}
                token2={token2}
                token1Amount={token1Amount}
                token2Amount={token2Amount}
                totalAmount={getDepositTotalAmount(network!, token1, token2, token1Amount, token2Amount, getUsdPrice)}
                key={'deposit-step'}
                minTick={getMinMaxTickState(userSelectedMinTick, minTick, userSelectedMaxTick, maxTick).minTickState}
                maxTick={getMinMaxTickState(userSelectedMinTick, minTick, userSelectedMaxTick, maxTick).maxTickState}
                isTokensReversedFromPool={isTokensReversedFromPool}
                handleTokenAmountChange={handleTokenAmountChange}
                currentTick={currentTick}
                handleToken1Select={handleToken1Select}
                handleToken2Select={handleToken2Select}
                stepNumber={stepNumber}
            />
        )
    }

    function renderOutOfRangeInput(token: string, isAbove: boolean) {
        const mxTick = userSelectedMaxTick || maxTick
        const mnTick = userSelectedMinTick || minTick
        const isError = (mxTick > currentTick && mnTick > currentTick) || (mxTick < currentTick && mnTick < currentTick)
        if (!isError) return null
        return (
            <div className='relative rounded-lg mt-2 p-4 text-[16px] h-[70px] font-bold bg-[transparent] overflow-hidden text-[white]'>
                <div className='absolute flex items-center p-4 justify-center left-0 top-0 bottom-0 right-0 w-full z-[1]'>
                    <span className=''>{`Your position is ${isAbove ? 'above' : 'below'} the current price, so you just need to add ${token} token`}</span>
                </div>
                <div className='bg-white absolute left-0 top-0 z-[0] w-full h-full opacity-[0.2]' />
            </div>
        )
    }

    function onUpdateUserMinTick(tick: number) {
        updateUrlSearchParams({ token1Address: token1, token2Address: token2, fee: fee, minTick: tick, maxTick: userSelectedMaxTick })
    }

    function onUpdateUserMaxTick(tick: number) {
        updateUrlSearchParams({ token1Address: token1, token2Address: token2, fee: fee, maxTick: tick, minTick: userSelectedMinTick })
    }

    function onSelectRange(range: number) {
        updateUrlSearchParams({ token1Address: token1, token2Address: token2, fee: fee, maxTick: undefined, minTick: undefined, range: range })
    }

    function renderStepRange() {
        return (
            <RangeStep
                onUpdateCurrentRatio={onUpdateCurrentRatio}
                isCreatePool={isCreatePoolPage}
                selectedPool={selectedPool}
                rangeOptions={rangeOptions}
                rangeSelected={rangeSelected}
                renderActionButton={renderActionButton as any}
                setCustomRange={setCustomRange}
                setIsMaxRangSelected={setIsMaxRangSelected}
                setIsRatioReversed={setIsRatioReversed}
                setMaxTick={onUpdateUserMaxTick}
                setMinTick={onUpdateUserMinTick}
                setRangeSelected={onSelectRange}
                stepNumber={stepNumber}
                minTick={userSelectedMinTick !== undefined ? userSelectedMinTick : minTick}
                isMaxRangeSelected={isMaxRangeSelected}
                isRatioReversed={isRatioReversed}
                maxTick={userSelectedMaxTick !== undefined ? userSelectedMaxTick : maxTick}
                currentRatio={currentRatio}
                currentTick={currentTick}
                customRange={customRange}
                fee={fee}
                isLoadingTicks={isLoadingTicks}
                tickAmount={tickAmount}
                token1={token1}
                token2={token2}
                updateMaxTick={updateMaxTick}
                updateMinTick={updateMinTick}
                updateMinTickBasedOnPrice={updateMinTickBasedOnPrice}
                updateMaxTickBasedOnPrice={updateMaxTickBasedOnPrice}
                key={'test'}
            />
        )
    }

    function renderStepIdentifier(number: number, isDisabled = false) {
        return (
            <div
                className={`${
                    isDisabled ? 'bg-[#366FB7] text-[#272756]' : 'bg-[#0AE8F9] text-[#272756]'
                } rounded-sm text-[18px] p-1 w-[20px] h-[20px] flex items-center justify-center`}>
                {number}
            </div>
        )
    }

    function renderCreatePoolDisclaimer() {
        return (
            <div className='flex items-center mb-6 justify-center text-[#0AE8F9] bg-[#0C3F6D] rounded-[4px] p-2'>
                <CreatePoolIcon className='mr-2 w-[16px] h-[16px]' />
                <span className='text-[14px]'>You're about to create a new pool! Follow these steps to get started:</span>
            </div>
        )
    }

    function renderIncentiveBadge(token1: string, token2: string) {
        const isMatch = possiblePools.some((pool) => [token1, token2].every((token) => pool.includes(token)))

        if (!isMatch) return null
        return (
            <div className='relative flex items-center ml-2'>
                <RewardBadgeIcon className='w-[24px] h-[24px]' />
                <span style={{ color: 'rgba(255, 255, 255, 0.50)' }} className='text-[12px] font-bold'>
                    (Eligible Pool)
                </span>
            </div>
        )
    }

    function renderSelect(isCreatePool: boolean, onSelect: (address: string) => void, selectedValue: string, options: Token[]) {
        return <Select isCreatePool={isCreatePool} size='sm' network={network!} onSelect={onSelect} placeholder={'Select Token'} selectedValue={selectedValue} options={options} />
    }

    function renderStepSelectTokens() {
        if (stepNumber < 1) return null
        return (
            <div className='flex flex-col'>
                <div className='flex items-center'>
                    {renderStepIdentifier(1, false)}
                    <span className='text-[18px] text-white font-bold ml-4'>Select Token Pair and pool fee</span>
                </div>
                <div className='mt-4 flex mb-4'>
                    <div className='mr-4'>{renderSelect(true, handleToken1Select, token1, getSortedList(tokens, token2, false) as any)}</div>
                    <div>{renderSelect(true, handleToken2Select, token2, getSortedList(tokens, token1, false) as any)}</div>
                    {renderIncentiveBadge(token1, token2)}
                </div>
                {isCreatePoolPage ? renderCreatePoolDisclaimer() : null}
            </div>
        )
    }

    function renderContent() {
        return (
            <div className='w-full lg:px-[80px]'>
                <BackButton onClick={() => navigate('/positions')} />
                <BoxHeaderNew slippage={slippage.toString()} setOutsideSlippage={(val) => setSlippage(val)} className='mb-[16px]' name='Add Liquidity' />
                <div className='flex w-full lg:flex-row flex-col'>
                    <div className='flex flex-col lg:w-[50%]'>
                        {renderStepSelectTokens()}
                        {renderStepFeeTier()}
                        {renderStepRange()}
                        {stepNumber < 3 ? renderActionButton() : null}
                        {stepNumber < 3 ? (
                            <div className='flex items-center mt-4'>
                                {renderStepIdentifier(2, true)}
                                <span className='text-[18px] text-[#3A4164] font-bold ml-4 opacity-[0.5]'>Set Price Range</span>
                            </div>
                        ) : null}
                    </div>
                    <div className='flex flex-col lg:w-[50%] lg:pl-[44px]'>{renderStepDeposit()}</div>
                </div>
            </div>
        )
    }

    function renderActionButton() {
        if (stepNumber === 2) return null

        const text = stepNumber > 3 ? 'Add' : 'Next'
        return (
            <div>
                {error ? (
                    <PrimaryButton className='mt-4' isDisabled={true}>
                        <span>{error}</span>
                    </PrimaryButton>
                ) : (
                    <PrimaryButton size='lg' isLoading={isTxInProgress} isDisabled={getIsNextDisabled()} className='mt-4' onClick={() => handleStepChange(true)}>
                        <span className='text-[16px]'>{text}</span>
                    </PrimaryButton>
                )}
            </div>
        )
    }

    return (
        <div className='h-full relative pb-[48px] lg:pb-0'>
            <div className='lg:pt-6 max-w-[98%] m-auto'>
                <Header className='' selectedTab={'pools'} />
            </div>
            <div className={`pb-[65px] container w-full p-4 mx-auto`}>
                <div className='relative flex w-full flex-col'>{renderContent()}</div>
            </div>
            <Footer selectedTab={'pools'} />
            <div style={pageStyle} className={`hidden lg:flex z-0 absolute h-full w-full bg-[#00176A] pointer-events-none`} />
            <div style={pageStyleMobile} className={`lg:hidden z-0 absolute h-full w-full bg-[#00176A] pointer-events-none`} />
        </div>
    )
}
